import { atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import request from 'lib/request';
import type {
  ProvisioningBatchesResponse,
  ProvisioningBatchResponse,
  ProvisioningBatchSubmissionsResponse,
  ProvisioningDocumentsResponse,
  ProvisioningStatsResponse,
  CreateBatchForm,
  CreateBatchResponse,
  CreateBatchErrorResponse,
  PreviewStats,
  PromptVersionsResponse,
  State,
  County,
  DocumentQueryResponse,
} from './types';

// Refresh trigger atoms
const batchesRefreshAtom = atom(0);
const statsRefreshAtom = atom(0);

// Filter state
export const batchFilterAtom = atom<{
  status: string | null;
  countyId: number | null;
  lookbackHours: number | null;
  page: number;
  perPage: number;
}>({
  status: null,
  countyId: null,
  lookbackHours: null,
  page: 1,
  perPage: 100,
});

// Base async atom for batches
const batchesBaseAtom = atom(async (get) => {
  get(batchesRefreshAtom);
  const filters = get(batchFilterAtom);

  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.countyId) params.append('county_id', filters.countyId.toString());
  if (filters.lookbackHours)
    params.append('lookback_hours', filters.lookbackHours.toString());
  params.append('page', filters.page.toString());
  params.append('per_page', filters.perPage.toString());

  const response = await request.get<ProvisioningBatchesResponse>(
    `/admin/provisioning_batches?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

// Loadable wrapper
const batchesLoadableAtom = loadable(batchesBaseAtom);

// Combined atom with refetch
export const provisioningBatchesAtom = atom(
  (get) => get(batchesLoadableAtom),
  (_get, set) => {
    set(batchesRefreshAtom, (c) => c + 1);
  }
);

// Stats atom
const statsBaseAtom = atom(async (get) => {
  get(statsRefreshAtom);
  const response = await request.get<ProvisioningStatsResponse>(
    '/admin/provisioning_batches/stats'
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

const statsLoadableAtom = loadable(statsBaseAtom);

export const provisioningStatsAtom = atom(
  (get) => get(statsLoadableAtom),
  (_get, set) => {
    set(statsRefreshAtom, (c) => c + 1);
  }
);

// Selected batch atom
export const selectedBatchIdAtom = atom<number | null>(null);

// Batch details atom (depends on selectedBatchId)
const batchDetailsRefreshAtom = atom(0);

const batchDetailsBaseAtom = atom(async (get) => {
  const batchId = get(selectedBatchIdAtom);
  get(batchDetailsRefreshAtom);

  if (!batchId) return null;

  const response = await request.get<ProvisioningBatchResponse>(
    `/admin/provisioning_batches/${batchId}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

const batchDetailsLoadableAtom = loadable(batchDetailsBaseAtom);

export const batchDetailsAtom = atom(
  (get) => get(batchDetailsLoadableAtom),
  (_get, set) => {
    set(batchDetailsRefreshAtom, (c) => c + 1);
  }
);

// Submission filter state (for batch-detail page)
export const submissionFilterAtom = atom<{
  page: number;
  perPage: number;
  search: string;
}>({
  page: 1,
  perPage: 100,
  search: '',
});

// Batch submissions atom
const batchSubmissionsRefreshAtom = atom(0);

const batchSubmissionsBaseAtom = atom(async (get) => {
  const batchId = get(selectedBatchIdAtom);
  const filters = get(submissionFilterAtom);
  get(batchSubmissionsRefreshAtom);

  if (!batchId) return null;

  const params = new URLSearchParams();
  params.append('page', filters.page.toString());
  params.append('per_page', filters.perPage.toString());
  if (filters.search) params.append('search', filters.search);

  const response = await request.get<ProvisioningBatchSubmissionsResponse>(
    `/admin/provisioning_batches/${batchId}/submissions?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

const batchSubmissionsLoadableAtom = loadable(batchSubmissionsBaseAtom);

export const batchSubmissionsAtom = atom(
  (get) => get(batchSubmissionsLoadableAtom),
  (_get, set) => {
    set(batchSubmissionsRefreshAtom, (c) => c + 1);
  }
);

// Selected submission ID atom
export const selectedSubmissionIdAtom = atom<number | null>(null);

// ─── Run Batch Modal Atoms ────────────────────────────────────────────────

// Whether the run batch modal is open (drives states fetch)
export const runModalOpenAtom = atom(false);

// Selected state ID within the run batch modal (drives counties fetch)
export const runModalStateIdAtom = atom<number | null>(null);

// States — only fetched while modal is open
const runModalStatesBaseAtom = atom(async (get): Promise<State[]> => {
  const isOpen = get(runModalOpenAtom);
  if (!isOpen) return [];

  const response = await request.get<State[]>('/states');
  if ('error' in response) throw new Error(response.error);
  return response;
});

export const runModalStatesAtom = loadable(runModalStatesBaseAtom);

// Counties — only fetched when a state is selected
const runModalCountiesBaseAtom = atom(async (get): Promise<County[]> => {
  const stateId = get(runModalStateIdAtom);
  if (!stateId) return [];

  const abortController = new AbortController();
  const response = await request.post<{ results: County[] }, { error: string }>(
    '/queries/counties',
    { query: { state_ids: [stateId], paginated: false } },
    abortController
  );

  if (response.type === 'error' && response.data?.error) {
    throw new Error(response.data.error);
  }
  if (response.type === 'success') return response.data.results;
  throw new Error('Failed to fetch counties');
});

export const runModalCountiesAtom = loadable(runModalCountiesBaseAtom);

// ─── Prompt Versions ──────────────────────────────────────────────────────────

const promptVersionsBaseAtom = atom(
  async (get): Promise<PromptVersionsResponse> => {
    const isOpen = get(runModalOpenAtom);
    if (!isOpen) return { promptVersions: [] };

    const response = await request.get<PromptVersionsResponse>(
      '/admin/prompt_versions'
    );
    if ('error' in response) throw new Error(response.error);
    return response;
  }
);

export const promptVersionsAtom = loadable(promptVersionsBaseAtom);

// ─── Preview Stats ────────────────────────────────────────────────────────────

// Form state atom written by the run batch modal so async preview atom can read it
export const previewFormAtom = atom<CreateBatchForm | null>(null);

const previewStatsBaseAtom = atom(async (get): Promise<PreviewStats | null> => {
  const form = get(previewFormAtom);
  if (!form) return null;

  const query: Record<string, unknown> = { text: '', page_size: 1 };
  if (form.countyId) query.county_ids = [form.countyId];
  if (form.stateId) query.state_ids = [form.stateId];
  if (form.provisioningStatus !== 'all')
    query.provisioning_status = form.provisioningStatus;
  if (form.documentType !== 'all') query.document_type = form.documentType;
  if (form.promptVersionIds.length > 0)
    query.prompt_ids = form.promptVersionIds;
  if (form.provisionedDateStart)
    query.provisioned_after = form.provisionedDateStart;
  if (form.provisionedDateEnd)
    query.provisioned_before = form.provisionedDateEnd;

  const abortController = new AbortController();
  const response = await request.post<DocumentQueryResponse, { error: string }>(
    '/queries/documents',
    { query },
    abortController
  );

  if (response.type !== 'success') {
    throw new Error('Failed to fetch preview');
  }

  const data = response.data;
  const statusBuckets = data.facets.provisioningStatus?.buckets ?? [];
  const facetProvisioned =
    statusBuckets.find((b) => b.key === 'provisioned')?.count ?? 0;
  const facetUnprovisioned =
    statusBuckets.find((b) => b.key === 'not_provisioned')?.count ?? 0;

  // When a provisioning_status filter is active, the facet buckets are unfiltered
  // (ES faceted search excludes each facet's own filter). Use the filter selection
  // + totalCount to derive the correct filtered counts.
  let provisioned: number;
  let unprovisioned: number;
  if (form.provisioningStatus === 'provisioned') {
    provisioned = data.totalCount;
    unprovisioned = 0;
  } else if (form.provisioningStatus === 'not_provisioned') {
    provisioned = 0;
    unprovisioned = data.totalCount;
  } else {
    provisioned = facetProvisioned;
    unprovisioned = facetUnprovisioned;
  }

  const promptBuckets = data.facets.promptIds?.buckets ?? [];
  // Derive "on latest prompt" count from prompt version atoms
  const promptVersionsResult = get(promptVersionsAtom);
  let onLatestPrompt: number | undefined;
  if (form.mode === 'provision' && promptVersionsResult.state === 'hasData') {
    const latestIds = new Set(
      promptVersionsResult.data.promptVersions
        .filter((p) => p.default)
        .map((p) => p.id)
    );
    onLatestPrompt = promptBuckets
      .filter((b) => latestIds.has(Number(b.key)))
      .reduce((sum, b) => sum + b.count, 0);
  }

  return {
    total: data.totalCount,
    unprovisioned,
    provisioned,
    withInterpretations: data.facets.hasInterpretationCount,
    onLatestPrompt,
  };
});

export const previewStatsAtom = loadable(previewStatsBaseAtom);

// ─── Create Batch Mutation ────────────────────────────────────────────────────

const createBatchStateAtom = atom<{
  loading: boolean;
  error: string | null;
  success: boolean;
}>({ loading: false, error: null, success: false });

export const createBatchMutationAtom = atom(
  (get) => get(createBatchStateAtom),
  async (_get, set, form: CreateBatchForm) => {
    set(createBatchStateAtom, { loading: true, error: null, success: false });

    const abortController = new AbortController();
    const response = await request.post<
      CreateBatchResponse,
      CreateBatchErrorResponse
    >(
      '/admin/provisioning_batches',
      {
        batch: {
          ...(form.countyId ? { county_id: form.countyId } : {}),
          mode: form.mode,
          parameters: {
            county_ids: form.countyId ? [form.countyId] : undefined,
            state_ids: form.stateId ? [form.stateId] : undefined,
            provisioning_status:
              form.provisioningStatus !== 'all'
                ? form.provisioningStatus
                : undefined,
            document_type:
              form.documentType !== 'all' ? form.documentType : undefined,
            prompt_ids:
              form.promptVersionIds.length > 0
                ? form.promptVersionIds
                : undefined,
            provisioned_after: form.provisionedDateStart ?? undefined,
            provisioned_before: form.provisionedDateEnd ?? undefined,
          },
        },
      },
      abortController
    );

    if (response.type === 'error' && response.data?.errors) {
      set(createBatchStateAtom, {
        loading: false,
        error: response.data.errors.join(', '),
        success: false,
      });
      return;
    }

    if (response.type === 'fatal' || response.type === 'abort') {
      set(createBatchStateAtom, {
        loading: false,
        error: response.message,
        success: false,
      });
      return;
    }

    if (response.type === 'success') {
      set(createBatchStateAtom, { loading: false, error: null, success: true });
      return;
    }

    set(createBatchStateAtom, {
      loading: false,
      error: 'Unexpected response',
      success: false,
    });
  }
);

export const resetCreateBatchAtom = atom(null, (_get, set) => {
  set(createBatchStateAtom, { loading: false, error: null, success: false });
});

// ─── Delete Batch Mutation ────────────────────────────────────────────────────

const deleteBatchStateAtom = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

export const deleteBatchMutationAtom = atom(
  (get) => get(deleteBatchStateAtom),
  async (_get, set, batchId: number) => {
    set(deleteBatchStateAtom, { loading: true, error: null });

    const response = await request.deleteReq<null, { error?: string }>(
      `/admin/provisioning_batches/${batchId}`,
      null,
      new AbortController()
    );

    if (response.type === 'success') {
      set(deleteBatchStateAtom, { loading: false, error: null });
      return true;
    }

    set(deleteBatchStateAtom, {
      loading: false,
      error:
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to delete batch',
    });
    return false;
  }
);

// ─── All Documents (batch-detail "Documents" tab) ────────────────────────────

export const allDocumentsFilterAtom = atom<{
  page: number;
  perPage: number;
  search: string;
}>({
  page: 1,
  perPage: 50,
  search: '',
});

const allDocumentsBaseAtom = atom(async (get) => {
  const batchId = get(selectedBatchIdAtom);
  const filters = get(allDocumentsFilterAtom);

  if (!batchId) return null;

  const params = new URLSearchParams();
  params.append('page', filters.page.toString());
  params.append('per_page', filters.perPage.toString());
  if (filters.search) params.append('search', filters.search);

  const response = await request.get<ProvisioningDocumentsResponse>(
    `/admin/provisioning_batches/${batchId}/documents?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

export const allDocumentsAtom = loadable(allDocumentsBaseAtom);

// ─── Sub-row Documents (expanded submission rows) ─────────────────────────────

// Per-submission pagination state (family key required by atomFamily but unused in body)
export const subDocsFilterAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- family key used by Jotai for atom identity
  (key: string) =>
    atom<{ page: number; perPage: number }>({ page: 1, perPage: 10 }),
  (a, b) => a === b
);

// Per-submission documents data (loadable)
export const subDocsAtomFamily = atomFamily(
  (params: { batchId: number; submissionId: number }) => {
    const key = `${params.batchId}-${params.submissionId}`;
    const baseAtom = atom(async (get) => {
      const filters = get(subDocsFilterAtomFamily(key));

      const searchParams = new URLSearchParams();
      searchParams.append('submission_id', params.submissionId.toString());
      searchParams.append('page', filters.page.toString());
      searchParams.append('per_page', filters.perPage.toString());

      const response = await request.get<ProvisioningDocumentsResponse>(
        `/admin/provisioning_batches/${
          params.batchId
        }/documents?${searchParams.toString()}`
      );
      if ('error' in response) {
        throw new Error(response.error);
      }
      return response;
    });
    return loadable(baseAtom);
  },
  (a, b) => a.batchId === b.batchId && a.submissionId === b.submissionId
);

// Document filter state (for submission-detail page)
export const documentFilterAtom = atom<{ page: number; perPage: number }>({
  page: 1,
  perPage: 100,
});

// Submission documents atom
const submissionDocumentsRefreshAtom = atom(0);

const submissionDocumentsBaseAtom = atom(async (get) => {
  const batchId = get(selectedBatchIdAtom);
  const submissionId = get(selectedSubmissionIdAtom);
  const filters = get(documentFilterAtom);
  get(submissionDocumentsRefreshAtom);

  if (!batchId || !submissionId) return null;

  const params = new URLSearchParams();
  params.append('submission_id', submissionId.toString());
  params.append('page', filters.page.toString());
  params.append('per_page', filters.perPage.toString());

  const response = await request.get<ProvisioningDocumentsResponse>(
    `/admin/provisioning_batches/${batchId}/documents?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

const submissionDocumentsLoadableAtom = loadable(submissionDocumentsBaseAtom);

export const submissionDocumentsAtom = atom(
  (get) => get(submissionDocumentsLoadableAtom),
  (_get, set) => {
    set(submissionDocumentsRefreshAtom, (c) => c + 1);
  }
);
